import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { CreditCard, BookOpenText } from "@phosphor-icons/react";
import { functions } from "@/config/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { isDemoPaymentBypassEmail } from "@/lib/demoPaymentBypass";
import { updateTakeStatus } from "@/lib/firestore/takes";
import { Button, Card, Eyebrow } from "@/components/ui";
import type { PricingTier, Take } from "@/types";

interface ProcessButtonProps {
  projectId: string;
  takes: Take[];
  readyCount: number;
  isRecording: boolean;
  isOverLimit: boolean;
  pricingTier: PricingTier;
  paidTiers: string[];
}

function productionStripeTier(pricingTier: PricingTier): "production_standard" | "production_premium" {
  return pricingTier === "premium" ? "production_premium" : "production_standard";
}

export function ProcessButton({
  projectId,
  takes,
  readyCount,
  isRecording,
  isOverLimit,
  pricingTier,
  paidTiers,
}: ProcessButtonProps) {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [processing, setProcessing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disabled = readyCount === 0 || isRecording || isOverLimit || processing;
  const hasProductionPayment = paidTiers.some((tier) => tier.startsWith("production_"));
  const hasDemoPaymentBypass = isDemoPaymentBypassEmail(user?.email);
  const canRunProduction = hasProductionPayment || hasDemoPaymentBypass;
  const stripeTier = productionStripeTier(pricingTier);

  const startProductionCheckout = async () => {
    setCheckoutLoading(true);
    setError(null);
    try {
      const createCheckoutSession = httpsCallable<
        { project_id: string; tier: typeof stripeTier; success_url: string; cancel_url: string },
        { checkout_url: string }
      >(functions, "createCheckoutSession");
      const baseUrl = window.location.origin;
      const result = await createCheckoutSession({
        project_id: projectId,
        tier: stripeTier,
        success_url: `${baseUrl}/project/${projectId}/studio?checkout=production-success`,
        cancel_url: `${baseUrl}/project/${projectId}/studio?checkout=production-cancelled`,
      });
      window.location.assign(result.data.checkout_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!confirming) { setConfirming(true); return; }

    setProcessing(true);
    setError(null);

    try {
      const readyTakes = takes.filter((t) => t.status === "ready");
      if (!canRunProduction) {
        throw new Error("Production payment is required before the press can run.");
      }
      const audioTakes = readyTakes.filter((t) => t.type !== "text_upload").sort((a, b) => a.order - b.order);
      const textTakes = readyTakes.filter((t) => t.type === "text_upload").sort((a, b) => a.order - b.order);

      // Mark all as processing
      await Promise.all(readyTakes.map((t) => updateTakeStatus(projectId, t.id, "processing")));

      // Call existing ingestion function — zero changes needed
      const processIngestion = httpsCallable(functions, "processIngestion");
      await processIngestion({
        project_id: projectId,
        audio_paths: audioTakes.map((t) => t.storage_path),
        text_paths: textTakes.map((t) => t.storage_path),
        audio_durations: audioTakes.map((t) => t.duration_seconds),
      });

      navigate(`/project/${projectId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The press could not run.");
      setProcessing(false);
      setConfirming(false);
    }
  };

  return (
    <div className="mt-12 border-t border-rule pt-10">
      {error && (
        <div className="mb-4 rounded-as-2 bg-flag-tint p-3 font-mono text-[11px] tracking-as-eyebrow uppercase text-flag">
          {error}
        </div>
      )}

      {confirming && !processing && (
        <Card variant="chapter" className="mb-6">
          {canRunProduction ? (
            <>
              <Eyebrow withDot>{hasDemoPaymentBypass && !hasProductionPayment ? "Demo bypass active" : "Press gate"}</Eyebrow>
              <h3 className="font-display font-semibold text-t-21 leading-as-snug text-ink-1 mt-[8px] mb-[8px]">
                Send {readyCount} source{readyCount !== 1 ? "s" : ""} into production.
              </h3>
              <p className="font-text text-t-16 leading-as-read text-ink-2">
                {hasDemoPaymentBypass && !hasProductionPayment
                  ? "This demo account can run production without Stripe checkout."
                  : "PullPress will transcribe, map, write, and prepare the manuscript path. Once submitted, the source lane closes until the proof is ready."}
              </p>
              <div className="mt-5 flex gap-3 items-center">
                <Button onClick={handleProcess} variant="walnut" size="md">
                  <BookOpenText size={16} weight="duotone" className="mr-2" />
                  Run the press
                </Button>
                <button
                  onClick={() => setConfirming(false)}
                  className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-2 hover:text-walnut-deep transition-colors duration-as-1"
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <Eyebrow withDot>Checkout required</Eyebrow>
              <h3 className="font-display font-semibold text-t-21 leading-as-snug text-ink-1 mt-[8px] mb-[8px]">
                Pay before production starts.
              </h3>
              <p className="font-text text-t-16 leading-as-read text-ink-2">
                Checkout opens the production lane for this book and protects press capacity for paid work.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                  onClick={startProductionCheckout}
                  disabled={checkoutLoading}
                  variant="walnut"
                  size="md"
                >
                  <CreditCard size={16} weight="duotone" className="mr-2" />
                  {checkoutLoading
                    ? "Opening checkout..."
                    : `Pay ${pricingTier === "premium" ? "$1,997" : "$997"} and run the press`}
                </Button>
                <button
                  onClick={() => setConfirming(false)}
                  className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-2 hover:text-walnut-deep transition-colors duration-as-1 self-start"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {!confirming && (
        <Button onClick={handleProcess} disabled={disabled} variant="primary" size="lg" className="w-full">
          <BookOpenText size={20} weight="duotone" className="mr-2" />
          {processing
            ? "Running the press..."
            : `Send ${readyCount} source${readyCount !== 1 ? "s" : ""} to the press`}
        </Button>
      )}

      {readyCount === 0 && !isRecording && (
        <p className="mt-3 text-center font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3">
          Bring in at least one source to begin.
        </p>
      )}
    </div>
  );
}
